import { useEffect, useRef, useState, useMemo } from "react";
import type { RoomChatMessage, RoomMember, RoomModAction } from "@gooner/shared";
import { ROOM_CHAT_MAX } from "@gooner/shared";
import { track } from "../../analytics";
import { MessageComposer } from "../../control/ui/MessageComposer";
import { MessageRow } from "../../control/ui/MessageRow";
import { RoomMemberMenu, targetOf } from "./RoomMemberMenu";
import type { RoomNote } from "./useRoom";
import type { RoomActivity } from "@gooner/shared";
import { GoldName } from "../../control/ui/Gold";

/** How far up from the bottom a reader may be and still be "at the bottom" — a new
 *  line scrolls them; further up, they are reading and it must not. */
const STICK_PX = 40;
/** Lines of one speaker this close together are one set: the face and name once, a line each. */
const GROUP_WINDOW_MS = 5 * 60_000;

/** What an activity line says after the actor's name. A repeat came back with a count. */
export function activityVerb(a: RoomActivity): string {
  const n = a.count ?? 1;
  switch (a.kind) {
    case "session_start":
      return "started a goon session";
    case "session_end":
      return a.minutes ? `ended a goon session after ${a.minutes} min` : "ended a goon session";
    case "edge":
      return n > 1 ? `edged ×${n}` : "edged";
    case "fap":
      return n > 1 ? `fapped ${n} clips` : "fapped a clip";
    case "repoint":
      return a.target ? `changed the view to ${a.target}` : "changed the view";
    case "pause":
      return "paused the room";
    case "resume":
      return "resumed the room";
    case "kick":
      return a.target ? `removed ${a.target}` : "removed someone";
    case "ban":
      return a.target ? `banned ${a.target}` : "banned someone";
  }
}

/**
 * The room's chat: the lines, newest at the bottom, and the box. Every row is the
 * Feed's MessageRow with the profile opening BESIDE the room (a room must not navigate
 * away — its socket dies with the page). No delete, no reply: a room's chat is a
 * conversation, not a thread, and it is gone when the room is. The HOST's click on a
 * name opens the same menu the roster's faces open (Profile / Camera off / Kick / Ban):
 * a name in the chat is where a host most often decides someone has to go.
 */
export function RoomChat({
  roomId,
  name,
  isPublic,
  watching,
  messages,
  notes = [],
  activity = [],
  onSend,
  portrait,
  disabled,
  error,
  isOwner,
  you,
  members,
  onMod,
  signedIn,
  onSignIn,
}: {
  roomId: string;
  name: string | null;
  isPublic: boolean;
  /** How many are in the room right now. */
  watching: number;
  messages: RoomChatMessage[];
  /** "x joined" / "x left", drawn grey in time order among the lines. */
  notes?: RoomNote[];
  /** What people did (the hub's lines), drawn grey with the actor's name as a chat name. */
  activity?: RoomActivity[];
  onSend: (body: string) => void;
  portrait: boolean;
  disabled?: boolean;
  error?: string | null;
  /** The host gets the member menu off a name; `you` and the host's own lines never open it. */
  isOwner: boolean;
  you: string | null;
  members: RoomMember[];
  onMod: (action: RoomModAction, username: string) => void;
  /** SIGNED OUT (2026-09-19): the lines arrive with their words veiled by the server (a
   *  block per letter) and are blurred here on top; the box is a sign-in door. */
  signedIn: boolean;
  onSignIn: () => void;
}) {
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const hostName = members.find((m) => m.owner)?.username ?? null;
  const menuable = (username: string) =>
    isOwner && username !== hostName && !members.some((m) => m.pid === you && m.username === username);
  const lines = useMemo(
    () =>
      [
        ...messages.map((m) => ({ at: m.at, key: m.id, msg: m, note: null, act: null })),
        ...notes.map((n) => ({ at: n.at, key: n.id, msg: null, note: n, act: null })),
        ...activity.map((a) => ({ at: a.at, key: a.id, msg: null, note: null, act: a })),
      ].sort((a, b) => a.at - b.at),
    [messages, notes, activity],
  );
  const listRef = useRef<HTMLDivElement | null>(null);
  const stuckRef = useRef(true);
  const [copied, setCopied] = useState(false);
  // THE LINK IS THE INVITE (owner, 2026-09-19): it sits at the top of the chat, one press
  // to copy, because "get someone in here" is the first thing a room wants and the address
  // bar is the wrong place to find it on a phone.
  const copyLink = () => {
    void navigator.clipboard.writeText(`${window.location.origin}/room/${roomId}`).catch(() => undefined);
    setCopied(true);
    track("room_link_copied", { public: isPublic });
    setTimeout(() => setCopied(false), 1500);
  };

  useEffect(() => {
    const el = listRef.current;
    if (!el || !stuckRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [lines]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--color-panel)]">
      <div className="flex items-center gap-2 border-b border-[var(--color-edge)] px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-zinc-100">{name ?? "Goon Room"}</p>
          <p className="text-[11px] text-zinc-500">
            {watching} watching{isPublic ? "" : " · private"}
          </p>
        </div>
        <button
          type="button"
          onClick={copyLink}
          title="Copy this room's link to share"
          className="shrink-0 rounded-md border border-[var(--color-edge)] px-2.5 py-1 text-xs text-zinc-200 transition-colors hover:bg-[var(--color-panel-2)]"
        >
          {copied ? "Copied" : "Share link"}
        </button>
      </div>
      <div
        ref={listRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stuckRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_PX;
        }}
        className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3"
      >
        {lines.length === 0 ? (
          <p className="text-xs text-zinc-500">Nothing yet — say the first thing.</p>
        ) : (
          lines.map(({ key, msg: m, note, act }, i) => {
            if (note) {
              return (
                <p key={key} className="text-xs text-zinc-500">
                  {note.text}
                </p>
              );
            }
            if (act) {
              return (
                <p key={key} className="text-xs text-zinc-500">
                  <a href={`/u/${encodeURIComponent(act.actor.username)}`} target="_blank" rel="noopener" className="font-medium hover:underline">
                    <GoldName name={act.actor.username} gold={act.actor.gold} />
                  </a>{" "}
                  {activityVerb(act)}
                </p>
              );
            }
            if (!m) return null;
            // ONE SET PER SPEAKER (owner, 2026-09-19): a line right after another of the
            // same person's is drawn under it — no face, no name again — as long as it
            // came within a few minutes and nothing else (a join, a leave) fell between.
            const prev = i > 0 ? lines[i - 1]! : null;
            const continues = !!prev?.msg && prev.msg.author.username === m.author.username && m.at - prev.at < GROUP_WINDOW_MS;
            const body = m.body ? (
              <p className={signedIn ? "whitespace-pre-wrap break-words text-sm text-zinc-200" : "select-none whitespace-pre-wrap break-words text-sm text-zinc-500 blur-[3px]"} aria-hidden={!signedIn}>
                {m.body}
              </p>
            ) : null;
            if (continues) {
              return (
                <div key={m.id} className="-mt-2 ml-9">
                  {body}
                </div>
              );
            }
            return (
            <MessageRow
              key={m.id}
              author={m.author}
              at={m.at}
              tag={m.author.username === hostName ? "(Host)" : undefined}
              profileInNewTab
              onNameClick={menuable(m.author.username) ? () => setMenuFor((cur) => (cur === m.id ? null : m.id)) : undefined}
              nameMenu={
                menuFor === m.id ? (
                  <RoomMemberMenu target={targetOf(m.author.username, members, m.author)} onClose={() => setMenuFor(null)} onMod={onMod} />
                ) : null
              }
            >
              {body}
            </MessageRow>
            );
          })
        )}
      </div>
      <div className="border-t border-[var(--color-edge)] p-2">
        {signedIn ? (
          <MessageComposer
            maxLength={ROOM_CHAT_MAX}
            placeholder={disabled ? "Chat is closed" : "Say something…"}
            onSend={onSend}
            disabled={disabled}
            error={error}
          />
        ) : (
          <button
            type="button"
            onClick={onSignIn}
            className="flex w-full items-center justify-between rounded-lg border border-[var(--color-accent)]/40 bg-[var(--color-panel-2)] px-3 py-2 text-left text-sm text-zinc-200 transition-colors hover:bg-[var(--color-panel)]"
          >
            <span>
              <span className="font-medium text-zinc-100">Sign in to read and join the chat</span>
              <span className="block text-xs text-zinc-500">Free — and your crown works in here too.</span>
            </span>
            <span className="rounded-md bg-[var(--color-accent)] px-2.5 py-1 text-xs font-semibold text-black">Sign in</span>
          </button>
        )}
      </div>
    </div>
  );
}
